import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, CheckPill } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Check, Plus, Trash2, Save, Calculator, ShieldCheck, FileDown, Upload, ChevronRight,
  ArrowLeft, ArrowRight, Layers, AlertTriangle, ChevronDown, Package2, FileSpreadsheet, GitBranch,
  Paperclip, Info,
} from "lucide-react";

// Obvious activities that belong in Energy or Water & Waste — used to prevent double counting
// when entered under Plant Operations. Keep simple (no rules engine).
const ENERGY_TERMS = ["electricity", "grid", "renewable", "diesel", "fuel", "petrol", "gas oil", "kwh"];
const WATER_TERMS = ["mixing water", "wash water", "process water", "water"];
const hasTerm = (s, terms) => terms.some((t) => (s || "").toLowerCase().includes(t));
import { toast } from "sonner";

const PROD_FIELDS = {
  cement: [["name", "Cement name"], ["cement_type", "Type / designation"], ["strength_class", "Strength class"],
    ["standard", "Product standard"], ["declared_unit", "Declared unit"], ["reference_period", "Reference period"]],
  concrete: [["name", "Product / mix name"], ["strength_class", "Strength class"], ["exposure_class", "Exposure class"],
    ["density", "Density"], ["declared_unit", "Declared unit"], ["reference_period", "Reference period"]],
};

// Section completion states (Phase 2). Saving alone never manufactures COMPLETE.
const STEP_UI = {
  complete:    { dot: "bg-emerald-600 text-white", label: "Complete", badge: "text-emerald-700" },
  in_progress: { dot: "bg-amber-500 text-white", label: "In progress", badge: "text-amber-700" },
  na:          { dot: "bg-slate-400 text-white", label: "Not applicable", badge: "text-slate-500" },
  not_started: { dot: "bg-slate-100 text-slate-500", label: "Not started", badge: "text-slate-400" },
  none:        { dot: "bg-slate-100 text-slate-500", label: "", badge: "" },
};
const NA_ELIGIBLE = ["transport", "energy", "manufacturing", "water_waste"];
const kgToT = (kg) => Math.round((Number(kg) || 0) / 1000 * 1e6) / 1e6;

export default function EpdWizard() {
  const { epdId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const [epd, setEpd] = useState(null);
  const [config, setConfig] = useState(null);
  const [active, setActive] = useState(sp.get("section") || "product_info");
  const [data, setData] = useState({});
  const [factors, setFactors] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [saving, setSaving] = useState(false);

  const canWrite = ["org_admin", "epd_manager", "data_contributor", "super_admin"].includes(user?.role);
  const isImmutable = epd?.current?.immutable;

  const load = useCallback(async () => {
    const [{ data: e }, { data: c }, fr, ev] = await Promise.all([
      api.get(`/epds/${epdId}`), api.get(`/epds/${epdId}/config`),
      api.get(`/factors`).catch(() => ({ data: [] })),
      api.get(`/epds/${epdId}/evidence`).catch(() => ({ data: [] })),
    ]);
    setEpd(e); setConfig(c); setData(e.current?.data || {}); setFactors(fr.data || []); setEvidence(ev.data || []);
  }, [epdId]);
  useEffect(() => { load(); }, [load]);

  if (!epd || !config) return <div className="p-8 text-sm text-muted-foreground">Loading EPD…</div>;

  const sections = config.sections;
  const cat = config.category;
  const ver = epd.current;
  const orderedKeys = sections.map((s) => s.key);
  const goNext = (key) => {
    const i = orderedKeys.indexOf(key);
    if (i >= 0 && i < orderedKeys.length - 1) setActive(orderedKeys[i + 1]);
  };

  const sectionStatus = (key) => {
    if (["evidence", "review", "calculate"].includes(key)) return "none";
    const d = data[key] || {};
    if (d.not_applicable) return "na";
    if (key === "product_info") {
      const pi = data.product_info || {};
      if (pi.name && pi.reference_period) return "complete";
      return (pi.name || pi.reference_period) ? "in_progress" : "not_started";
    }
    if (key === "composition") {
      const rows = d.rows || [];
      if (!rows.length) return "not_started";
      return rows.every((r) => Number(r.quantity) > 0 && (r.label || "").trim()) ? "complete" : "in_progress";
    }
    if (key === "raw_materials") {
      const comp = data.composition?.rows || [];
      if (!comp.length) return "not_started";
      const byId = Object.fromEntries((d.rows || []).map((r) => [r.input_id, r]));
      const accepted = comp.filter((c) => byId[c.input_id]?.mapping_status === "accepted").length;
      if (accepted === 0) return "in_progress";
      return accepted === comp.length ? "complete" : "in_progress";
    }
    if (key === "transport") {
      const comp = data.composition?.rows || [];
      const byId = Object.fromEntries((d.rows || []).map((r) => [r.input_id, r]));
      const applicable = comp.filter((c) => !byId[c.input_id]?.not_applicable);
      if (!comp.length) return (d.rows || []).length ? "in_progress" : "not_started";
      if (!applicable.length) return "na";
      const done = applicable.every((c) => Number(byId[c.input_id]?.distance_km) > 0 && (byId[c.input_id]?.mode || "").trim());
      const any = applicable.some((c) => Number(byId[c.input_id]?.distance_km) > 0);
      return done ? "complete" : (any ? "in_progress" : "not_started");
    }
    if (key === "energy") {
      const rows = d.rows || [];
      if (!rows.length) return "not_started";
      const ok = rows.every((r) => Number(r.period_quantity) > 0 && Number(r.production_quantity) > 0);
      const any = rows.some((r) => Number(r.period_quantity) > 0 || Number(r.production_quantity) > 0);
      return ok ? "complete" : (any ? "in_progress" : "not_started");
    }
    const rows = d.rows || [];
    if (!rows.length) return "not_started";
    return rows.every((r) => Number(r.quantity) > 0 && (r.label || "").trim()) ? "complete" : "in_progress";
  };

  const saveSection = async (key, payload, silent) => {
    setSaving(true);
    try {
      await api.put(`/epds/${epdId}/data/${key}`, { data: payload });
      await load();
      if (!silent) toast.success("Saved");
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
    finally { setSaving(false); }
  };

  // Save & Continue: validate -> save -> confirm -> navigate. On validation failure, stay + identify issue.
  const saveAndContinue = async (key, payload, validate) => {
    if (validate) { const msg = validate(); if (msg) { toast.error(msg); return; } }
    const ok = await saveSection(key, payload);
    if (ok) goNext(key);
  };

  return (
    <div>
      <button onClick={() => nav("/app/epds")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3">
        <ArrowLeft className="h-4 w-4" /> All EPDs
      </button>
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-semibold">{epd.name}</h1>
            <StatusBadge status={ver.status} />
            {ver.epd_public_id && <span className="font-mono text-sm text-slate-600">{ver.epd_public_id}</span>}
            {epd.is_demo && <span className="text-[10px] rounded bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5">DEMO</span>}
            {epd.project_mode === "TEST" && <span data-testid="test-badge" className="text-[10px] rounded bg-orange-100 border border-orange-300 text-orange-800 px-1.5 py-0.5 font-semibold">TEST / DEMONSTRATION</span>}
            {epd.project_mode === "TEST" && epd.category === "concrete" && (
              <button data-testid="load-demo-concrete" onClick={async () => { try { await api.post(`/epds/${epdId}/load-demo-concrete`); toast.success("Demo concrete data loaded"); window.location.reload(); } catch (e) { toast.error(apiError(e)); } }}
                className="text-xs rounded border border-emerald-300 text-emerald-800 bg-emerald-50 px-2 py-0.5 hover:bg-emerald-100">Load Demo Concrete Data</button>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {epd.product?.name} · {cat === "cement" ? "Cement" : "Ready-Mix Concrete"} · {epd.plant?.name} · V{ver.version}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right mr-2">
            <div className="text-xs text-muted-foreground">Data Completion</div>
            <div className="font-mono text-lg font-semibold text-emerald-700">{epd.data_completion}%</div>
          </div>
          <WorkflowActions epd={epd} ver={ver} user={user} epdId={epdId} onChange={load} />
        </div>
      </div>

      <NextStep status={ver.status} onGo={setActive} />

      <div className="grid grid-cols-12 gap-6">
        {/* Stepper */}
        <div className="col-span-12 lg:col-span-3">
          <div className="bg-white border rounded-lg p-2 sticky top-20">
            {sections.map((s, i) => {
              const st = sectionStatus(s.key);
              const ui = STEP_UI[st] || STEP_UI.none;
              return (
              <button key={s.key} data-testid={`step-${s.key}`} data-status={st} onClick={() => setActive(s.key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-left transition-colors ${active === s.key ? "bg-emerald-50 text-emerald-900" : "hover:bg-slate-50"}`}>
                <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-mono shrink-0 ${st === "complete" ? ui.dot : active === s.key ? "bg-emerald-100 text-emerald-700" : ui.dot}`}>
                  {st === "complete" ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                <span className="flex-1">
                  {s.label}
                  {ui.label && !["evidence", "review", "calculate"].includes(s.key) &&
                    <span className={`block text-[10px] font-medium ${ui.badge}`} data-testid={`step-status-${s.key}`}>{ui.label}</span>}
                </span>
                {s.module !== "-" && s.module !== "A0" && <span className="text-[10px] font-mono text-slate-400">{s.module}</span>}
              </button>
            );})}
          </div>
        </div>

        {/* Content */}
        <div className="col-span-12 lg:col-span-9">
          {active === "product_info" && <ProductInfo cat={cat} data={data.product_info || {}} canWrite={canWrite && !isImmutable}
            onSaveContinue={(p) => saveAndContinue("product_info", p, () => (!p.name || !p.reference_period)
              ? "Enter Product name and Reference period before continuing" : null)} saving={saving} config={config} />}
          {active === "composition" &&
            <RowSection sectionKey="composition" label={sections.find((s) => s.key === "composition")?.label} cat={cat} config={config}
              rows={data.composition?.rows || []} canWrite={canWrite && !isImmutable} saving={saving}
              onSaveContinue={(rows) => saveAndContinue("composition", { rows }, () => {
                if (!rows.length) return "Add at least one material to the Mix Design before continuing";
                const bad = rows.find((r) => !(Number(r.quantity) > 0) || !(r.label || "").trim());
                return bad ? `Enter a name and positive quantity for every material (check "${bad.label || "unnamed row"}")` : null;
              })} epdId={epdId} onImported={load} />}
          {active === "energy" &&
            <EnergyA3Section rows={data.energy?.rows || []} na={!!data.energy?.not_applicable} factors={factors} evidence={evidence}
              canWrite={canWrite && !isImmutable} saving={saving}
              onSaveContinue={(rows) => saveAndContinue("energy", { rows })}
              onSaveNA={(na) => saveSection("energy", { rows: data.energy?.rows || [], not_applicable: na })} />}
          {active === "manufacturing" &&
            <RowSection sectionKey="manufacturing" label={sections.find((s) => s.key === "manufacturing")?.label} cat={cat} config={config}
              rows={data.manufacturing?.rows || []} na={!!data.manufacturing?.not_applicable} naEligible canWrite={canWrite && !isImmutable} saving={saving}
              banner={<><b>Additional A3 manufacturing activity only.</b> Enter here only manufacturing inputs/activities NOT already captured in Energy, Water &amp; Waste, Mix Design or Transport. Do not re-enter electricity, diesel or water here.</>}
              onSaveContinue={(rows) => saveAndContinue("manufacturing", { rows }, () => {
                const dup = rows.find((r) => hasTerm(r.label, ENERGY_TERMS) || hasTerm(r.label, WATER_TERMS));
                return dup ? `"${dup.label}" looks like an Energy or Water & Waste activity — enter it in that section to avoid double counting.` : null;
              })}
              onSaveNA={(na) => saveSection("manufacturing", { rows: data.manufacturing?.rows || [], not_applicable: na })}
              epdId={epdId} onImported={load} />}
          {active === "water_waste" &&
            <RowSection sectionKey="water_waste" label={sections.find((s) => s.key === "water_waste")?.label} cat={cat} config={config}
              rows={data.water_waste?.rows || []} na={!!data.water_waste?.not_applicable} naEligible canWrite={canWrite && !isImmutable} saving={saving}
              banner={<><b>Additional plant water &amp; waste streams only.</b> Mixing water is already in the canonical Mix Design ({(data.composition?.rows || []).filter((r) => r.key === "water").map((r) => `${r.quantity} ${r.unit}`).join(", ") || "e.g. 180 kg/m³"}) and must NOT be re-entered here. Capture process/wash/recycled water and waste/returned concrete with treatment/route.</>}
              onSaveContinue={(rows) => saveAndContinue("water_waste", { rows }, () => {
                const dup = rows.find((r) => (r.label || "").toLowerCase().includes("mixing water"));
                return dup ? `Mixing water is already counted in Mix Design — remove "${dup.label}" to avoid double counting.` : null;
              })}
              onSaveNA={(na) => saveSection("water_waste", { rows: data.water_waste?.rows || [], not_applicable: na })}
              epdId={epdId} onImported={load} />}
          {active === "raw_materials" &&
            <RawMaterialsMapping compositionRows={data.composition?.rows || []} overlay={data.raw_materials?.rows || []}
              factors={factors} evidence={evidence} canWrite={canWrite && !isImmutable} saving={saving}
              onSaveContinue={(rows) => saveAndContinue("raw_materials", { rows })} />}
          {active === "transport" &&
            <TransportSection compositionRows={data.composition?.rows || []} overlay={data.transport?.rows || []} evidence={evidence}
              na={!!data.transport?.not_applicable} canWrite={canWrite && !isImmutable} saving={saving}
              onSaveContinue={(rows) => saveAndContinue("transport", { rows })}
              onSaveNA={(na) => saveSection("transport", { rows: data.transport?.rows || [], not_applicable: na })} />}
          {active === "evidence" && <EvidencePanel epdId={epdId} canWrite={canWrite} data={data} onChange={load} />}
          {active === "review" && <ValidationPanel epdId={epdId} onChange={load} />}
          {active === "calculate" && <CalculatePanel epdId={epdId} ver={ver} canWrite={canWrite && !isImmutable} onChange={load} config={config} />}
        </div>
      </div>
    </div>
  );
}

/* ---------- Product info ---------- */
function ProductInfo({ cat, data, canWrite, onSaveContinue, saving }) {
  const [form, setForm] = useState(data);
  useEffect(() => { setForm(data); }, [data]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="bg-white border rounded-lg p-6">
      <h3 className="font-medium mb-1">Product definition</h3>
      <p className="text-sm text-muted-foreground mb-5">Core product information for this EPD. The declared unit is {cat === "cement" ? "1 tonne of cement" : "1 m³ of concrete"}.</p>
      <div className="grid grid-cols-2 gap-4">
        {PROD_FIELDS[cat].map(([k, label]) => (
          <div key={k}>
            <Label className="text-xs">{label}</Label>
            <Input data-testid={`pinfo-${k}`} value={form[k] || ""} disabled={!canWrite} onChange={(e) => set(k, e.target.value)} className="mt-1" />
          </div>
        ))}
        <div className="col-span-2">
          <Label className="text-xs">Description</Label>
          <Textarea data-testid="pinfo-description" value={form.description || ""} disabled={!canWrite} onChange={(e) => set("description", e.target.value)} className="mt-1" />
        </div>
        {cat === "cement" && (
          <div className="col-span-2 border-t pt-4">
            <Label className="text-xs">Clinker methodology (double-counting control)</Label>
            <select data-testid="pinfo-clinker_method" value={form.clinker_method || "A_gross"} disabled={!canWrite}
              onChange={(e) => set("clinker_method", e.target.value)}
              className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
              <option value="A_gross">Method A — Integrated / gross clinker dataset (embeds calcination + kiln fuel)</option>
              <option value="B_disaggregated">Method B — Disaggregated clinker (calcination + kiln fuels + inputs entered separately)</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              {form.clinker_method === "B_disaggregated"
                ? "Enter calcination (process CO₂) and kiln fuels separately. Do NOT also use a gross clinker factor — the engine will block that combination."
                : "Kiln fuels are already embedded in the gross clinker dataset. Do NOT re-enter kiln fuels (coal/petcoke/HFO) under Energy — only finishing/plant energy. The engine blocks double-counting combinations."}
            </p>
          </div>
        )}
      </div>
      {canWrite && <div className="mt-5"><Button data-testid="save-product-info" onClick={() => onSaveContinue(form)} disabled={saving}><Save className="h-4 w-4 mr-1.5" /> Save &amp; continue</Button></div>}
    </div>
  );
}

/* ---------- Row-based section ---------- */
function RowSection({ sectionKey, label, cat, config, rows, canWrite, onSaveContinue, onSaveNA, na, naEligible, saving, epdId, onImported, banner }) {
  const [local, setLocal] = useState(rows);
  const [importOpen, setImportOpen] = useState(false);
  useEffect(() => { setLocal(rows); }, [rows]);
  const catalogGroup = sectionKey === "composition" ? "composition" : sectionKey === "energy" ? "energy" : null;
  const catalog = config.material_catalog?.[catalogGroup] || [];
  const usedKeys = new Set(local.map((r) => r.key));
  const available = catalog.filter((c) => !usedKeys.has(c.key));

  const addRow = (item) => setLocal((l) => [...l, item ? { key: item.key, label: item.label, quantity: "", unit: item.unit }
    : { key: "custom_" + Date.now(), label: "", quantity: "", unit: "" }]);
  const update = (i, k, v) => setLocal((l) => l.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const remove = (i) => setLocal((l) => l.filter((_, idx) => idx !== i));

  if (naEligible && na) {
    return (
      <div className="bg-white border rounded-lg p-6" data-testid={`${sectionKey}-na-panel`}>
        <h3 className="font-medium">{label}</h3>
        <div className="mt-3 flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-sm text-slate-600"><b>Marked Not Applicable.</b> This section is explicitly confirmed as not applicable to this product and is excluded from the declaration.</div>
          {canWrite && <Button size="sm" variant="outline" data-testid={`${sectionKey}-undo-na`} onClick={() => onSaveNA(false)} disabled={saving}>Mark applicable</Button>}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-medium">{label}</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            {sectionKey === "composition" ? (cat === "cement" ? "Constituent quantities per declared unit (1 tonne)." : "Mix design quantities per m³ — the single canonical material inventory.")
              : sectionKey === "energy" ? "Energy and fuel inputs per declared unit." : "Enter quantities with units and notes."}
          </p>
        </div>
        {canWrite && catalogGroup && (
          <Button size="sm" variant="outline" data-testid={`import-${sectionKey}`} onClick={() => setImportOpen(true)}>
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel import
          </Button>
        )}
      </div>
      {banner && <div data-testid={`${sectionKey}-purpose`} className="mb-4 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900 flex gap-2"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" /><div>{banner}</div></div>}

      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-slate-500 border-b">
          <th className="py-2 pr-2">Item</th><th className="py-2 px-2 w-28">Quantity</th>
          <th className="py-2 px-2 w-24">Unit</th><th className="py-2 px-2">Notes</th><th className="w-10" />
        </tr></thead>
        <tbody>
          {local.map((r, i) => (
            <tr key={i} data-testid={`${sectionKey}-row-${i}`} className="border-b last:border-0">
              <td className="py-1.5 pr-2">
                <Input value={r.label} disabled={!canWrite} onChange={(e) => update(i, "label", e.target.value)} className="h-8" />
              </td>
              <td className="py-1.5 px-2"><Input data-testid={`${sectionKey}-qty-${i}`} type="number" value={r.quantity} disabled={!canWrite} onChange={(e) => update(i, "quantity", e.target.value)} className="h-8 font-mono" /></td>
              <td className="py-1.5 px-2"><Input value={r.unit || ""} disabled={!canWrite} onChange={(e) => update(i, "unit", e.target.value)} className="h-8" /></td>
              <td className="py-1.5 px-2"><Input value={r.note || ""} disabled={!canWrite} onChange={(e) => update(i, "note", e.target.value)} className="h-8" placeholder="source / assumption" /></td>
              <td className="py-1.5">{canWrite && <button data-testid={`${sectionKey}-del-${i}`} onClick={() => remove(i)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}</td>
            </tr>
          ))}
          {local.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-sm text-muted-foreground">No rows yet.</td></tr>}
        </tbody>
      </table>

      {canWrite && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {available.map((c) => (
            <button key={c.key} data-testid={`add-${sectionKey}-${c.key}`} onClick={() => addRow(c)}
              className="text-xs border rounded-full px-3 py-1 hover:bg-slate-50 flex items-center gap-1">
              <Plus className="h-3 w-3" /> {c.label}
            </button>
          ))}
          <button data-testid={`add-${sectionKey}-custom`} onClick={() => addRow(null)} className="text-xs border border-dashed rounded-full px-3 py-1 hover:bg-slate-50 flex items-center gap-1">
            <Plus className="h-3 w-3" /> Custom row
          </button>
        </div>
      )}
      {canWrite && (
        <div className="mt-5 flex items-center gap-2">
          <Button data-testid={`save-${sectionKey}`} onClick={() => onSaveContinue(local)} disabled={saving}><Save className="h-4 w-4 mr-1.5" /> Save &amp; continue</Button>
          {naEligible && <Button variant="outline" data-testid={`${sectionKey}-mark-na`} onClick={() => onSaveNA(true)} disabled={saving}>Mark not applicable</Button>}
        </div>
      )}

      <ImportDialog open={importOpen} onOpenChange={setImportOpen} cat={cat} epdId={epdId} onImported={onImported} />
    </div>
  );
}

/* ---------- Raw Materials A1 — dataset mapping (inherits Mix Design) ---------- */
function RawMaterialsMapping({ compositionRows, overlay, factors, evidence, canWrite, saving, onSaveContinue }) {
  const initial = () => {
    const byId = Object.fromEntries((overlay || []).map((r) => [r.input_id, r]));
    return Object.fromEntries((compositionRows || []).map((c) => {
      const m = byId[c.input_id] || {};
      return [c.input_id, { factor_id: m.factor_id || "", mapping_status: m.mapping_status || (m.factor_id ? "candidate" : "unmapped") }];
    }));
  };
  const [map, setMap] = useState(initial);
  useEffect(() => { setMap(initial()); /* eslint-disable-next-line */ }, [JSON.stringify(overlay), JSON.stringify(compositionRows)]);
  const [prov, setProv] = useState(null);
  const [ev, setEv] = useState(() => Object.fromEntries((overlay || []).map((r) => [r.input_id, r.evidence_ids || []])));
  useEffect(() => { setEv(Object.fromEntries((overlay || []).map((r) => [r.input_id, r.evidence_ids || []]))); /* eslint-disable-next-line */ }, [JSON.stringify(overlay)]);
  const factorDoc = (id) => (factors || []).find((x) => (x.id || x._id) === id);

  const optionsFor = (key) => (factors || []).filter((f) => f.key === key);
  const factorName = (id) => { const f = (factors || []).find((x) => (x.id || x._id) === id); return f ? (f.material || f.dataset_name) : null; };
  const setRow = (iid, patch) => setMap((m) => ({ ...m, [iid]: { ...m[iid], ...patch } }));

  const displayStatus = (iid, key) => {
    const m = map[iid] || {};
    if (m.mapping_status === "accepted" && m.factor_id) return "ACCEPTED";
    if (m.factor_id) return "CANDIDATE";
    return optionsFor(key).length ? "CANDIDATE" : "UNMAPPED";
  };
  const badge = (s) => s === "ACCEPTED" ? "bg-emerald-50 border-emerald-200 text-emerald-700"
    : s === "CANDIDATE" ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-slate-50 border-slate-200 text-slate-600";

  const save = () => {
    const rows = (compositionRows || []).map((c) => {
      const m = map[c.input_id] || {};
      return { input_id: c.input_id, key: c.key, label: c.label, quantity: c.quantity, unit: c.unit,
        designation: c.designation || c.note, factor_id: m.factor_id || null, evidence_ids: ev[c.input_id] || [],
        mapping_status: (m.mapping_status === "accepted" && m.factor_id) ? "accepted" : (m.factor_id ? "candidate" : "unmapped") };
    });
    onSaveContinue(rows);
  };

  if (!compositionRows?.length) {
    return (
      <div className="bg-white border rounded-lg p-6" data-testid="raw_materials-empty">
        <h3 className="font-medium">Raw Materials (A1) — environmental dataset mapping</h3>
        <p className="text-sm text-muted-foreground mt-2">Enter the <b>Mix Design</b> first. The material inventory is inherited from Mix Design — you map each inherited material to its environmental dataset here (quantities are never re-entered).</p>
      </div>
    );
  }

  return (
    <div className="bg-white border rounded-lg p-6" data-testid="raw-materials-mapping">
      <h3 className="font-medium">Raw Materials (A1) — environmental dataset mapping</h3>
      <p className="text-sm text-muted-foreground mt-0.5 mb-4">
        Materials and quantities are <b>inherited read-only from Mix Design</b>. Map each inherited input to its environmental dataset.
        An auto-suggested dataset remains a <b>CANDIDATE</b> until you explicitly accept it. Unmapped inputs stay visibly UNMAPPED.
      </p>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-slate-500 border-b">
          <th className="py-2 pr-2">Material (inherited)</th><th className="py-2 px-2 w-24">Quantity</th>
          <th className="py-2 px-2">Environmental dataset</th><th className="py-2 px-2 w-32">Status</th><th className="py-2 px-2 w-40">Action</th>
        </tr></thead>
        <tbody>
          {compositionRows.map((c) => {
            const iid = c.input_id; const m = map[iid] || {}; const opts = optionsFor(c.key);
            const st = displayStatus(iid, c.key);
            const hasStoredOpt = !!m.factor_id && opts.some((f) => (f.id || f._id) === m.factor_id);
            return (
              <tr key={iid} data-testid={`a1-map-${iid}`} className="border-b last:border-0 align-top">
                <td className="py-2 pr-2">
                  <div className="font-medium">{c.label}</div>
                  <div className="text-[11px] text-slate-400 font-mono">{iid}{c.designation ? ` · ${c.designation}` : (c.note ? ` · ${c.note}` : "")}</div>
                </td>
                <td className="py-2 px-2 font-mono text-xs whitespace-nowrap">{c.quantity} {c.unit}</td>
                <td className="py-2 px-2">
                  <select data-testid={`a1-select-${iid}`} disabled={!canWrite} value={m.factor_id || ""}
                    onChange={(e) => setRow(iid, { factor_id: e.target.value, mapping_status: e.target.value ? "candidate" : "unmapped" })}
                    className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs">
                    <option value="">{opts.length ? "— select dataset —" : "No dataset available (UNMAPPED)"}</option>
                    {m.factor_id && !hasStoredOpt && <option value={m.factor_id}>{factorName(m.factor_id) || "Currently mapped dataset"} (retained)</option>}
                    {opts.map((f) => <option key={f.id || f._id} value={f.id || f._id}>{f.material || f.dataset_name}{f.demo_only ? " (DEMO)" : ""} · {f.source} {f.dataset_status === "APPROVED" ? "" : `(${f.dataset_status || f.status})`}</option>)}
                  </select>
                  {m.factor_id && <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2">
                    <span>{factorName(m.factor_id)}</span>
                    <button type="button" data-testid={`a1-prov-${iid}`} onClick={() => setProv(prov === iid ? null : iid)} className="inline-flex items-center gap-0.5 text-sky-700 hover:underline"><Info className="h-3 w-3" /> provenance</button>
                  </div>}
                  {prov === iid && m.factor_id && <Provenance f={factorDoc(m.factor_id)} mappingStatus={st} testid={iid} />}
                  {evidence && <div className="mt-1"><EvidenceLinker value={ev[iid] || []} evidence={evidence} canWrite={canWrite} testid={iid} onChange={(ids) => setEv((e) => ({ ...e, [iid]: ids }))} /></div>}
                </td>
                <td className="py-2 px-2"><span data-testid={`a1-status-${iid}`} className={`text-[11px] rounded border px-2 py-0.5 ${badge(st)}`}>{st}</span></td>
                <td className="py-2 px-2">
                  {canWrite && (
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`a1-accept-${iid}`}
                        disabled={!m.factor_id || m.mapping_status === "accepted"}
                        onClick={() => setRow(iid, { mapping_status: "accepted" })}>Accept</Button>
                      {(m.factor_id || m.mapping_status !== "unmapped") &&
                        <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`a1-unmap-${iid}`}
                          onClick={() => setRow(iid, { factor_id: "", mapping_status: "unmapped" })}>Unmap</Button>}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {canWrite && <div className="mt-5"><Button data-testid="save-raw_materials" onClick={save} disabled={saving}><Save className="h-4 w-4 mr-1.5" /> Save &amp; continue</Button></div>}
    </div>
  );
}

/* ---------- Evidence linker + provenance (Phase 3) ---------- */
function EvidenceLinker({ value, evidence, canWrite, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const ids = value || [];
  if (!evidence?.length) return <span className="text-[11px] text-slate-400">No evidence uploaded yet</span>;
  const toggle = (id) => onChange(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  return (
    <div className="relative inline-block">
      <button type="button" data-testid={`evlink-${testid}`} onClick={() => setOpen((o) => !o)}
        className="text-[11px] border rounded px-2 py-0.5 hover:bg-slate-50 inline-flex items-center gap-1">
        <Paperclip className="h-3 w-3" /> Evidence ({ids.length})
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-64 max-h-56 overflow-auto rounded-md border bg-white p-2 shadow-lg">
          {evidence.map((doc) => (
            <label key={doc.id} className="flex items-start gap-2 py-1 text-xs cursor-pointer hover:bg-slate-50 rounded px-1">
              <input type="checkbox" data-testid={`evopt-${testid}-${doc.id}`} disabled={!canWrite} checked={ids.includes(doc.id)} onChange={() => toggle(doc.id)} className="mt-0.5" />
              <span><span className="font-medium">{doc.original_filename}</span><span className="block text-[10px] text-slate-400">{doc.category}</span></span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function Provenance({ f, mappingStatus, testid }) {
  if (!f) return <div className="text-[11px] text-slate-500 mt-1">Provenance not available.</div>;
  const np = (v) => (v === 0 || v ? v : "not provided");
  const dtype = f.dataset_type || f.representativeness || (f.demo_only ? "demo / illustrative" : "not provided");
  const dstatus = f.dataset_status || (f.demo_only ? "DEMO" : (f.status === "approved" ? "APPROVED" : (f.status ? String(f.status).toUpperCase() : "not provided")));
  const rows = [
    ["Dataset", np(f.material || f.dataset_name)], ["Source / provider", np(f.provider || f.source)],
    ["Manufacturer", np(f.manufacturer)], ["Geography", np(f.geography)],
    ["Reference year", np(f.reference_year)], ["Version", np(f.database_version || f.source_version)],
    ["Dataset type", dtype], ["Dataset status", dstatus], ["Factor ID", np(f.id || f._id)],
    ["Mapping status", np(mappingStatus)], ["Unit", np(f.unit)],
    ["GWP-total (kg CO₂e/unit)", np(f.indicators?.["GWP-total"])],
  ];
  return (
    <div data-testid={`a1-provenance-${testid}`} className="mt-2 rounded-md border bg-slate-50 p-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
      {rows.map(([k, v]) => <div key={k}><span className="text-slate-400">{k}: </span><span className={v === "not provided" ? "text-slate-400 italic" : "text-slate-700"}>{String(v)}</span></div>)}
    </div>
  );
}

/* ---------- Energy A3 — plant-period normalization ---------- */
function EnergyA3Section({ rows, na, factors, evidence, canWrite, saving, onSaveContinue, onSaveNA }) {
  const CAT = [
    { key: "nigeria_grid", label: "Electricity — Nigeria national grid", unit: "kWh" },
    { key: "renewable_purchased", label: "Electricity — purchased renewable", unit: "kWh" },
    { key: "electricity", label: "Grid electricity (unspecified)", unit: "kWh" },
    { key: "diesel", label: "Diesel (loaders/plant)", unit: "kg" },
  ];
  const [local, setLocal] = useState(rows);
  useEffect(() => { setLocal(rows); /* eslint-disable-next-line */ }, [JSON.stringify(rows)]);
  const add = (item) => setLocal((l) => [...l, { key: item ? item.key : "custom_" + Date.now(), label: item ? item.label : "", unit: item ? item.unit : "",
    period_quantity: "", period_start: "", period_end: "", production_quantity: "", production_unit: "m³", factor_id: "", evidence_ids: [] }]);
  const upd = (i, k, v) => setLocal((l) => l.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const rm = (i) => setLocal((l) => l.filter((_, idx) => idx !== i));
  const alloc = (r) => (Number(r.period_quantity) > 0 && Number(r.production_quantity) > 0)
    ? Math.round(Number(r.period_quantity) / Number(r.production_quantity) * 1e6) / 1e6 : null;
  const optionsFor = (key) => (factors || []).filter((f) => f.key === key);
  const save = () => onSaveContinue(local.map((r) => { const a = alloc(r); return { ...r, quantity: a == null ? "" : a }; }));

  if (na) return (
    <div className="bg-white border rounded-lg p-6" data-testid="energy-na-panel">
      <h3 className="font-medium">Energy (A3)</h3>
      <div className="mt-3 flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="text-sm text-slate-600"><b>Marked Not Applicable.</b> No plant energy/fuel is allocated to this product.</div>
        {canWrite && <Button size="sm" variant="outline" data-testid="energy-undo-na" onClick={() => onSaveNA(false)} disabled={saving}>Mark applicable</Button>}
      </div>
    </div>
  );

  return (
    <div className="bg-white border rounded-lg p-6" data-testid="energy-section">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-medium">Energy &amp; fuel (module A3) — plant-period allocation</h3>
          <p className="text-sm text-muted-foreground mt-0.5 mb-4">Enter the plant's total energy/fuel for a reporting period and the concrete produced in the <b>same period</b>. Allocation per m³ is computed deterministically (total ÷ production). Original totals are preserved. Missing production is DATA MISSING — never a false zero.</p>
        </div>
        {canWrite && <Button size="sm" variant="outline" data-testid="energy-mark-na" onClick={() => onSaveNA(true)} disabled={saving}>Mark section N/A</Button>}
      </div>
      <div className="space-y-3">
        {local.map((r, i) => { const a = alloc(r); const opts = optionsFor(r.key); return (
          <div key={i} data-testid={`energy-row-${i}`} className="rounded-md border p-3">
            <div className="flex items-center gap-2 mb-2">
              <Input value={r.label} disabled={!canWrite} onChange={(e) => upd(i, "label", e.target.value)} className="h-8 flex-1" placeholder="energy/fuel source" />
              {canWrite && <button data-testid={`energy-del-${i}`} onClick={() => rm(i)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div><Label className="text-[11px]">Period total</Label><Input data-testid={`energy-period-${i}`} type="number" value={r.period_quantity} disabled={!canWrite} onChange={(e) => upd(i, "period_quantity", e.target.value)} className="h-8 font-mono mt-0.5" /></div>
              <div><Label className="text-[11px]">Unit</Label><Input data-testid={`energy-unit-${i}`} value={r.unit || ""} disabled={!canWrite} onChange={(e) => upd(i, "unit", e.target.value)} className="h-8 mt-0.5" placeholder="kWh / kg" /></div>
              <div><Label className="text-[11px]">Period start</Label><Input data-testid={`energy-start-${i}`} type="date" value={r.period_start || ""} disabled={!canWrite} onChange={(e) => upd(i, "period_start", e.target.value)} className="h-8 mt-0.5" /></div>
              <div><Label className="text-[11px]">Period end</Label><Input data-testid={`energy-end-${i}`} type="date" value={r.period_end || ""} disabled={!canWrite} onChange={(e) => upd(i, "period_end", e.target.value)} className="h-8 mt-0.5" /></div>
              <div><Label className="text-[11px]">Concrete produced (same period)</Label><Input data-testid={`energy-prod-${i}`} type="number" value={r.production_quantity} disabled={!canWrite} onChange={(e) => upd(i, "production_quantity", e.target.value)} className="h-8 font-mono mt-0.5" /></div>
              <div><Label className="text-[11px]">Production unit</Label><Input value={r.production_unit || "m³"} disabled={!canWrite} onChange={(e) => upd(i, "production_unit", e.target.value)} className="h-8 mt-0.5" /></div>
              <div><Label className="text-[11px]">Allocated / m³</Label><div data-testid={`energy-alloc-${i}`} className="h-8 mt-0.5 flex items-center font-mono text-xs">{a == null ? <span className="text-amber-600">DATA MISSING</span> : `${a} ${r.unit || ""}`}</div></div>
              <div><Label className="text-[11px]">Dataset</Label>
                <select data-testid={`energy-dataset-${i}`} disabled={!canWrite} value={r.factor_id || ""} onChange={(e) => upd(i, "factor_id", e.target.value)} className="w-full h-8 mt-0.5 rounded-md border border-input bg-background px-2 text-xs">
                  <option value="">{opts.length ? "auto (by type)" : "no dataset"}</option>
                  {opts.map((f) => <option key={f.id || f._id} value={f.id || f._id}>{f.material || f.dataset_name}{f.demo_only ? " (DEMO)" : ""}</option>)}
                </select>
              </div>
            </div>
            {evidence && <div className="mt-2"><EvidenceLinker value={r.evidence_ids || []} evidence={evidence} canWrite={canWrite} testid={`energy-${i}`} onChange={(ids) => upd(i, "evidence_ids", ids)} /></div>}
          </div>
        );})}
        {local.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">No energy/fuel sources yet.</div>}
      </div>
      {canWrite && <div className="mt-4 flex flex-wrap items-center gap-2">
        {CAT.map((c) => <button key={c.key} data-testid={`add-energy-${c.key}`} onClick={() => add(c)} className="text-xs border rounded-full px-3 py-1 hover:bg-slate-50 flex items-center gap-1"><Plus className="h-3 w-3" /> {c.label}</button>)}
        <button data-testid="add-energy-custom" onClick={() => add(null)} className="text-xs border border-dashed rounded-full px-3 py-1 hover:bg-slate-50 flex items-center gap-1"><Plus className="h-3 w-3" /> Custom</button>
      </div>}
      {canWrite && <div className="mt-5 flex items-center gap-2">
        <Button data-testid="save-energy" onClick={save} disabled={saving}><Save className="h-4 w-4 mr-1.5" /> Save &amp; continue</Button>
      </div>}
    </div>
  );
}

/* ---------- Transport (A2) — inherits masses from Mix Design ---------- */
function TransportSection({ compositionRows, overlay, na, canWrite, onSaveContinue, onSaveNA, saving, evidence }) {
  const initial = () => {
    const byId = Object.fromEntries((overlay || []).map((r) => [r.input_id, r]));
    return Object.fromEntries((compositionRows || []).map((c) => {
      const m = byId[c.input_id] || {};
      return [c.input_id, { distance_km: m.distance_km ?? "", mode: m.mode ?? "", origin: m.origin ?? "", not_applicable: !!m.not_applicable, evidence_ids: m.evidence_ids || [] }];
    }));
  };
  const [rows, setRows] = useState(initial);
  useEffect(() => { setRows(initial()); /* eslint-disable-next-line */ }, [JSON.stringify(overlay), JSON.stringify(compositionRows)]);
  const setRow = (iid, patch) => setRows((r) => ({ ...r, [iid]: { ...r[iid], ...patch } }));

  const save = () => {
    const out = (compositionRows || []).map((c) => {
      const r = rows[c.input_id] || {};
      return { input_id: c.input_id, key: "road_transport", label: c.label,
        mass_t: kgToT(c.quantity), distance_km: r.not_applicable ? "" : r.distance_km,
        mode: r.not_applicable ? "" : (r.mode || "road"), origin: r.origin || "", not_applicable: !!r.not_applicable, evidence_ids: r.evidence_ids || [] };
    });
    onSaveContinue(out);
  };

  if (na) {
    return (
      <div className="bg-white border rounded-lg p-6" data-testid="transport-na-panel">
        <h3 className="font-medium">Transport (module A2)</h3>
        <div className="mt-3 flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-sm text-slate-600"><b>Marked Not Applicable.</b> No inbound transport is declared for this product.</div>
          {canWrite && <Button size="sm" variant="outline" data-testid="transport-undo-na" onClick={() => onSaveNA(false)} disabled={saving}>Mark applicable</Button>}
        </div>
      </div>
    );
  }

  if (!compositionRows?.length) {
    return (
      <div className="bg-white border rounded-lg p-6" data-testid="transport-empty">
        <h3 className="font-medium">Transport (module A2)</h3>
        <p className="text-sm text-muted-foreground mt-2">Enter the <b>Mix Design</b> first. Transport masses are inherited from Mix Design — you only supply distance, mode and origin.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border rounded-lg p-6" data-testid="transport-section">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-medium">Transport (module A2)</h3>
          <p className="text-sm text-muted-foreground mt-0.5 mb-4">
            Masses are <b>inherited read-only from Mix Design</b> (kg → t). Supply the actual distance, mode and origin for each input.
            Nothing is assumed — an input with no distance is not counted. Mark inputs sourced on-site as <b>Not applicable</b>.
          </p>
        </div>
        {canWrite && <Button size="sm" variant="outline" data-testid="transport-mark-na" onClick={() => onSaveNA(true)} disabled={saving}>Mark section N/A</Button>}
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-slate-500 border-b">
          <th className="py-2 pr-2">Input (inherited)</th><th className="py-2 px-2 w-32">Mass</th>
          <th className="py-2 px-2 w-28">Distance (km)</th><th className="py-2 px-2 w-24">Mode</th>
          <th className="py-2 px-2">Origin / source</th><th className="py-2 px-2 w-24">N/A</th>
        </tr></thead>
        <tbody>
          {compositionRows.map((c) => {
            const iid = c.input_id; const r = rows[iid] || {};
            return (
              <tr key={iid} data-testid={`a2-row-${iid}`} className="border-b last:border-0">
                <td className="py-1.5 pr-2"><div className="font-medium">{c.label}</div><div className="text-[11px] text-slate-400 font-mono mt-0.5">{iid}</div></td>
                <td className="py-1.5 px-2 font-mono text-xs whitespace-nowrap">{c.quantity} kg = {kgToT(c.quantity)} t</td>
                <td className="py-1.5 px-2"><Input data-testid={`a2-dist-${iid}`} type="number" value={r.distance_km} disabled={!canWrite || r.not_applicable} onChange={(e) => setRow(iid, { distance_km: e.target.value })} className="h-8 font-mono" /></td>
                <td className="py-1.5 px-2"><Input data-testid={`a2-mode-${iid}`} value={r.mode} disabled={!canWrite || r.not_applicable} onChange={(e) => setRow(iid, { mode: e.target.value })} className="h-8" placeholder="road" /></td>
                <td className="py-1.5 px-2"><Input data-testid={`a2-origin-${iid}`} value={r.origin} disabled={!canWrite || r.not_applicable} onChange={(e) => setRow(iid, { origin: e.target.value })} className="h-8" placeholder="supplier / quarry" />
                  {evidence && <div className="mt-1"><EvidenceLinker value={r.evidence_ids || []} evidence={evidence} canWrite={canWrite && !r.not_applicable} testid={`a2-${iid}`} onChange={(ids) => setRow(iid, { evidence_ids: ids })} /></div>}
                </td>
                <td className="py-1.5 px-2"><input type="checkbox" data-testid={`a2-na-${iid}`} checked={!!r.not_applicable} disabled={!canWrite} onChange={(e) => setRow(iid, { not_applicable: e.target.checked })} className="h-4 w-4" /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {canWrite && <div className="mt-5"><Button data-testid="save-transport" onClick={save} disabled={saving}><Save className="h-4 w-4 mr-1.5" /> Save &amp; continue</Button></div>}
    </div>
  );
}

/* ---------- Evidence ---------- */
function EvidencePanel({ epdId, canWrite, data, onChange }) {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("production records");
  const [desc, setDesc] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/epds/${epdId}/evidence`).then(({ data }) => setItems(data));
  useEffect(() => { load(); api.get("/evidence/categories").then(({ data }) => setCats(data)); }, [epdId]);
  const linkMap = {};
  ["raw_materials", "transport", "energy", "water_waste"].forEach((sec) => ((data?.[sec]?.rows) || []).forEach((r) => ((r.evidence_ids) || []).forEach((id) => { (linkMap[id] = linkMap[id] || []).push(r.label || r.input_id || sec); })));
  const upload = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("category", cat); fd.append("description", desc);
    try { await api.post(`/epds/${epdId}/evidence`, fd); toast.success("Evidence uploaded"); setFile(null); setDesc(""); load(); onChange?.(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/evidence/${id}`); load(); onChange?.(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="bg-white border rounded-lg p-6">
      <h3 className="font-medium mb-1">Evidence Vault</h3>
      <p className="text-sm text-muted-foreground mb-5">Supporting documents remain associated with this EPD project. PDF, Excel, CSV, images and office documents accepted.</p>
      {canWrite && (
        <div className="border rounded-lg p-4 bg-slate-50 mb-5 grid md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-1">
            <Label className="text-xs">Category</Label>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger data-testid="evidence-category" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs">File</Label>
            <Input data-testid="evidence-file" type="file" onChange={(e) => setFile(e.target.files[0])} className="mt-1 bg-white" />
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs">Description</Label>
            <Input data-testid="evidence-desc" value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1 bg-white" />
          </div>
          <Button data-testid="evidence-upload" onClick={upload} disabled={!file || busy}><Upload className="h-4 w-4 mr-1.5" /> Upload</Button>
        </div>
      )}
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-slate-500 border-b">
          <th className="py-2">File</th><th className="py-2">Category</th><th className="py-2">Uploaded by</th>
          <th className="py-2">Date</th><th className="py-2 text-right">Action</th>
        </tr></thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} data-testid={`evidence-item-${it.id}`} className="border-b last:border-0">
              <td className="py-2.5"><a href={`${API}/files/${it.id}/download`} target="_blank" rel="noreferrer" className="text-primary hover:underline">{it.original_filename}</a>
                {(linkMap[it.id] || []).length > 0 && <div className="text-[10px] text-emerald-700" data-testid={`ev-links-${it.id}`}>Supports: {(linkMap[it.id]).join(", ")}</div>}
              </td>
              <td className="py-2.5 text-slate-600">{it.category}</td>
              <td className="py-2.5 text-slate-600">{it.uploader}</td>
              <td className="py-2.5 text-xs text-slate-500">{(it.created_at || "").slice(0, 10)}</td>
              <td className="py-2.5 text-right">{canWrite && <button data-testid={`evidence-del-${it.id}`} onClick={() => del(it.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No evidence uploaded yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Calculate & Results ---------- */
function CalculatePanel({ epdId, ver, canWrite, onChange, config }) {
  const [calc, setCalc] = useState(ver.calculation);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  useEffect(() => { setCalc(ver.calculation); }, [ver.calculation]);

  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/epds/${epdId}/calculate`); setCalc(data); toast.success(data.complete ? "Calculation complete" : "Calculation preview updated — incomplete"); onChange(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const meta = config.indicator_meta;
  return (
    <div className="space-y-5">
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium">LCA Calculation — cradle-to-gate (A1–A3)</h3>
            <p className="text-sm text-muted-foreground mt-0.5">Deterministic and reproducible. Results are activity data × traceable emission factors. No values are AI-generated or fabricated.</p>
          </div>
          {canWrite && <Button data-testid="run-calculation" onClick={run} disabled={busy}><Calculator className="h-4 w-4 mr-1.5" /> {calc ? "Recalculate" : "Run calculation"}</Button>}
        </div>
      </div>

      {calc && (
        <>
          {calc.complete === false && (
            <div data-testid="calc-preview-incomplete" className="bg-amber-50 border border-amber-300 rounded-lg p-4 text-sm text-amber-900">
              <div className="font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Calculation Preview — Incomplete</div>
              <p className="mt-1">This is a diagnostic A1 preview using available (incl. candidate) data — <b>not</b> a complete cradle-to-gate A1–A3 result. It cannot be submitted for review or issued.</p>
              <ul className="list-disc ml-5 mt-1.5">
                {(calc.unresolved_inputs || []).length > 0 && <li data-testid="preview-unresolved">{calc.unresolved_inputs.length} A1 input(s) not accepted: {calc.unresolved_inputs.map((u) => `${u.label} (${u.status})`).join(", ")}</li>}
                {calc.module_data_status?.A2 !== "OK" && <li>A2 transport — DATA MISSING (not counted as zero)</li>}
                {calc.module_data_status?.A3 !== "OK" && <li>A3 manufacturing — DATA MISSING (not counted as zero)</li>}
              </ul>
            </div>
          )}
          {calc.uses_demo_factors && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> This calculation uses DEMO ONLY factors — permitted for draft but blocked from final issuance.
            </div>
          )}
          {calc.gaps?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              <b>{calc.gaps.length} input(s) have no emission factor</b> and were excluded: {calc.gaps.map((g) => g.label).join(", ")}.
            </div>
          )}

          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h3 className="font-medium">Environmental performance (per declared unit){calc.complete === false && <span data-testid="gwp-preview-tag" className="ml-2 text-[11px] rounded border border-amber-300 bg-amber-50 text-amber-700 px-1.5 py-0.5 align-middle">PREVIEW — INCOMPLETE</span>}</h3>
              <span className="font-mono text-sm">GWP-total{calc.complete === false ? " (A1 preview)" : ""}: <b>{calc.total_gwp}</b> kg CO₂e</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b"><tr className="text-left text-xs text-slate-500">
                <th className="px-4 py-2">Indicator</th><th className="px-4 py-2">Unit</th>
                <th className="px-4 py-2 text-right">A1</th><th className="px-4 py-2 text-right">A2</th>
                <th className="px-4 py-2 text-right">A3</th><th className="px-4 py-2 text-right">A1–A3</th>
              </tr></thead>
              <tbody>
                {config.indicator_codes.map((code) => {
                  const total = calc.totals[code];
                  const pm = calc.results_per_module[code];
                  const mds = calc.module_data_status || {};
                  return (
                    <tr key={code} data-testid={`indicator-${code}`} className="border-b last:border-0">
                      <td className="px-4 py-2 font-mono text-xs">{code}<div className="text-[10px] text-slate-400 font-sans">{meta[code]?.label}</div></td>
                      <td className="px-4 py-2 text-xs text-slate-500">{meta[code]?.unit}</td>
                      {total == null ? <td colSpan={4} className="px-4 py-2 text-center text-xs text-slate-400">n/a — no complete factor data (not estimated)</td> : <>
                        <td className="px-4 py-2 text-right font-mono text-xs">{fmt(pm?.A1)}</td>
                        <td className="px-4 py-2 text-right font-mono text-xs" data-testid={`mod-a2-${code}`}>{mds.A2 === "OK" ? fmt(pm?.A2) : <span className="text-amber-600">DATA MISSING</span>}</td>
                        <td className="px-4 py-2 text-right font-mono text-xs" data-testid={`mod-a3-${code}`}>{mds.A3 === "OK" ? fmt(pm?.A3) : <span className="text-amber-600">DATA MISSING</span>}</td>
                        <td className="px-4 py-2 text-right font-mono text-xs font-semibold">{fmt(total)}{calc.complete === false ? "*" : ""}</td>
                      </>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="bg-white border rounded-lg p-5">
            <h3 className="font-medium mb-3 flex items-center gap-2"><Layers className="h-4 w-4" /> Contribution analysis (GWP-total) — click a row for provenance</h3>
            <div className="space-y-1.5">
              {calc.contribution_analysis.map((c, i) => (
                <div key={i}>
                  <button data-testid={`contribution-${i}`} onClick={() => setExpanded(expanded === c.label ? null : c.label)}
                    className="w-full flex items-center gap-3 hover:bg-slate-50 rounded px-2 py-1.5 text-left">
                    <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${expanded === c.label ? "rotate-180" : ""}`} />
                    <span className="w-40 text-sm truncate">{c.label}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${c.percent}%` }} /></div>
                    <span className="font-mono text-xs w-14 text-right">{c.percent}%</span>
                    <span className="font-mono text-xs w-24 text-right text-slate-500">{fmt(c.gwp)}</span>
                  </button>
                  {expanded === c.label && (
                    <div className="ml-6 my-2 border-l-2 border-emerald-200 pl-4 space-y-2">
                      {calc.provenance.filter((p) => p.label === c.label).map((p, j) => (
                        <div key={j} data-testid={`provenance-${i}-${j}`} className="text-xs bg-slate-50 rounded p-3">
                          <div className="font-mono">
                            {fmt(p.activity_value)} {p.activity_unit} × {p.factor_value} {"("}kg CO₂e/{p.factor_unit}{")"} = <b>{fmt(p.result)} kg CO₂e</b>
                          </div>
                          <div className="text-slate-500 mt-1">
                            Module {p.module} · Factor: {p.factor_name} · Source: {p.factor_source} ({p.factor_reference_year}, {p.factor_version})
                            {p.factor_demo_only && <span className="ml-1 text-amber-700">· DEMO ONLY</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border rounded-lg p-5">
            <h3 className="font-medium mb-3">Emission factors used (traceable)</h3>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500 border-b">
                <th className="py-2">Material / process</th><th className="py-2">Source</th>
                <th className="py-2">Year</th><th className="py-2">Status</th>
              </tr></thead>
              <tbody>
                {calc.factor_snapshot.map((f, i) => {
                  const isCand = (calc.input_resolution || []).some((r) => r.status !== "ACCEPTED" && r.factor_name === f.material);
                  return (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2">{f.material}</td>
                    <td className="py-2 text-slate-600 text-xs">{f.source}</td>
                    <td className="py-2 text-xs">{f.reference_year}</td>
                    <td className="py-2"><span data-testid={`factor-status-${i}`} className={`text-xs rounded border px-2 py-0.5 ${f.demo_only || isCand ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>{f.demo_only ? "DEMO ONLY" : isCand ? "CANDIDATE / PROXY" : (f.dataset_status || f.status)}</span></td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- Validation & readiness ---------- */
function ValidationPanel({ epdId, onChange }) {
  const [result, setResult] = useState(null);
  const [elig, setElig] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/epds/${epdId}/validate`); setResult(data);
      const { data: e } = await api.get(`/epds/${epdId}/issuance-check`); setElig(e);
      onChange && onChange();
    }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  useEffect(() => { run(); }, [epdId]);
  return (
    <div className="space-y-5">
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium">Automated technical validation & EPD readiness</h3>
            <p className="text-sm text-muted-foreground mt-0.5">Checks completeness, units, mass balance, factors, PCR indicators, evidence and outliers. Automated validation is not independent verification.</p>
          </div>
          <Button variant="outline" data-testid="run-validation" onClick={run} disabled={busy}>Re-run checks</Button>
        </div>
      </div>
      {elig && (
        <div data-testid="issuance-check" className={`border rounded-lg p-4 ${elig.eligible ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
          <div className="text-sm font-medium mb-1">{elig.eligible ? "✓ Eligible for issuance" : "Issuance gate — not yet eligible"}</div>
          {!elig.eligible && <ul className="list-disc ml-5 text-sm text-amber-800">{elig.failures.map((f, i) => <li key={i} data-testid={`issuance-fail-${i}`}>{f}</li>)}</ul>}
        </div>
      )}
      {result && (
        <>
          <div className="bg-white border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium">EPD Readiness</h3>
              <div className="text-right">
                <span className="font-mono text-2xl font-semibold text-emerald-700">{result.readiness.overall_pct}%</span>
                <span className="ml-2 text-sm">{result.readiness.level}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(result.readiness.sections).map(([k, v]) => (
                <div key={k} className="border rounded-lg p-3">
                  <div className="text-xs text-muted-foreground">{k}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${v}%` }} /></div>
                    <span className="font-mono text-xs">{v}%</span>
                  </div>
                </div>
              ))}
            </div>
            {result.blocking_count > 0 && <p className="mt-4 text-sm text-red-700">{result.blocking_count} blocking error(s) must be resolved before this EPD can move to review.</p>}
          </div>
          <div className="bg-white border rounded-lg p-5">
            <h3 className="font-medium mb-3">Validation checks</h3>
            <div className="space-y-2">
              {result.checks.map((c) => (
                <div key={c.key} data-testid={`check-${c.key}`} className="flex items-start justify-between gap-4 border-b last:border-0 pb-2">
                  <div>
                    <div className="text-sm font-medium">{c.label}</div>
                    <div className="text-xs text-muted-foreground">{c.message}</div>
                  </div>
                  <CheckPill status={c.status} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- Excel import dialog ---------- */
function ImportDialog({ open, onOpenChange, cat, epdId, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const doPreview = async () => {
    if (!file) return; setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/epds/${epdId}/import`, fd); setPreview(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const confirm = async () => {
    try { await api.post(`/epds/${epdId}/import/confirm`, { preview: preview.preview }); toast.success("Import confirmed"); onOpenChange(false); setPreview(null); setFile(null); onImported && onImported(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Excel import — {cat}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <a href={`${API}/templates/${cat}`} className="text-sm text-primary hover:underline inline-flex items-center gap-1"><FileDown className="h-4 w-4" /> Download {cat} template</a>
          <div className="flex gap-2 items-end">
            <div className="flex-1"><Label className="text-xs">Upload completed template</Label><Input data-testid="import-file" type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files[0])} className="mt-1" /></div>
            <Button data-testid="import-validate" onClick={doPreview} disabled={!file || busy}>Validate</Button>
          </div>
          {preview && (
            <div className="text-sm space-y-2">
              <div className="flex gap-4 text-xs">
                <span className="text-emerald-700">Accepted: {preview.accepted_count}</span>
                <span className="text-amber-700">Warnings: {preview.warning_count}</span>
                <span className="text-red-700">Rejected: {preview.rejected_count}</span>
              </div>
              {preview.rejected?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded p-2 text-xs">
                  {preview.rejected.map((r, i) => <div key={i}>Rejected {r.key}: {r.reason}</div>)}
                </div>
              )}
              {preview.warnings?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                  {preview.warnings.map((r, i) => <div key={i}>{r.key}: {r.reason}</div>)}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="import-confirm" onClick={confirm} disabled={!preview || preview.accepted_count === 0}>Confirm import</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Next-step guidance banner ---------- */
function NextStep({ status, onGo }) {
  const map = {
    DRAFT: { label: "Enter product data to begin.", cta: "Go to Product", to: "product_info" },
    DATA_COLLECTION: { label: "Continue entering data, then run the LCA calculation.", cta: "Go to Calculate", to: "calculate" },
    CALCULATION_COMPLETE: { label: "Calculation complete. Run automated technical validation.", cta: "Run technical validation", to: "review" },
    REVIEW_READY: { label: "Validation passed. Submit for internal review and approval.", cta: "Go to review & issue", to: "review" },
    EXTERNAL_VERIFICATION: { label: "Awaiting external verification. An admin records the verifier's details and documentation.", cta: "Go to verification", to: "review" },
    APPROVED: { label: "Approved. Issue the EPD to generate its permanent ID, PDF and QR-linked public declaration record.", cta: "Go to issue", to: "review" },
    ISSUED: { label: "Issued. Download the final PDF or open the public declaration record.", cta: "Go to issue", to: "review" },
  };
  const m = map[status];
  if (!m) return null;
  return (
    <div data-testid="next-step" className="mb-6 flex items-center justify-between gap-4 rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-3">
      <div className="text-sm text-emerald-900"><span className="font-medium">Next step:</span> {m.label}</div>
      {onGo && m.to && <Button size="sm" variant="outline" data-testid="next-step-cta" onClick={() => onGo(m.to)}>{m.cta} <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>}
    </div>
  );
}

/* ---------- Workflow actions (state machine) ---------- */
function WorkflowActions({ epd, ver, user, epdId, onChange }) {
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [indepOpen, setIndepOpen] = useState(false);
  const [pv, setPv] = useState(null);
  useEffect(() => {
    api.get(`/epds/${epdId}/platform-validation`).then(({ data }) => setPv(data)).catch(() => setPv(null));
  }, [epdId, ver.status, ver.calculation]);
  const role = user?.role;
  const isAdmin = role === "org_admin" || role === "super_admin";
  const isManager = isAdmin || role === "epd_manager";
  const isReviewer = isAdmin || role === "reviewer";
  const s = ver.status;

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onChange(); } catch (e) { toast.error(apiError(e)); } };

  const openPdf = async (path, method = "get") => {
    try {
      const { data } = method === "post"
        ? await api.post(path, {}, { responseType: "blob" })
        : await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      let msg = apiError(e);
      if (e?.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text())?.detail || msg; } catch { /* keep msg */ }
      }
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-3">
      <PlatformValidationPanel pv={pv} />
      <div className="flex items-center gap-2 flex-wrap">
      {ver.calculation && isManager && (s === "CALCULATION_COMPLETE" || s === "REVIEW_READY" || s === "EXTERNAL_VERIFICATION" || s === "APPROVED") && (
        <Button size="sm" variant="outline" data-testid="draft-pdf-btn" onClick={() => openPdf(`/epds/${epdId}/draft-pdf`, "post")}>
          <FileDown className="h-3.5 w-3.5 mr-1" /> Preview PDF
        </Button>
      )}
      {isManager && (ver.calculation || s === "REVIEW_READY" || pv?.platform_validated) && (
        <Button size="sm" data-testid="submit-independent-verification-btn" disabled={!pv?.platform_validated}
          title={pv?.platform_validated ? "" : "Platform Validation must pass first"} onClick={() => setIndepOpen(true)}>
          <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Submit for Independent Verification
        </Button>
      )}
      {isManager && s === "CALCULATION_COMPLETE" && (
        <Button size="sm" data-testid="submit-review-btn" onClick={() => act(() => api.post(`/epds/${epdId}/submit-review`), "Submitted for internal review")}>
          Submit for review <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      )}
      {isReviewer && s === "REVIEW_READY" && (
        <>
          <Button size="sm" variant="outline" data-testid="review-return-btn" onClick={() => act(() => api.post(`/epds/${epdId}/review`, { decision: "return", comments: "Returned for correction" }), "Returned for correction")}>Return</Button>
          <Button size="sm" data-testid="review-approve-btn" onClick={() => act(() => api.post(`/epds/${epdId}/review`, { decision: "approve", comments: "Approved for external verification" }), "Approved for external verification")}>Approve for verification</Button>
        </>
      )}
      {isManager && s === "EXTERNAL_VERIFICATION" && (
        <Button size="sm" variant="outline" data-testid="verification-package-btn" onClick={async () => {
          try { const { data } = await api.get(`/epds/${epdId}/verification-package`);
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob); const a = document.createElement("a");
            a.href = url; a.download = `verification-package-${epdId}.json`; a.click(); URL.revokeObjectURL(url);
          } catch (e) { toast.error(apiError(e)); }
        }}><Package2 className="h-3.5 w-3.5 mr-1" /> Verification package</Button>
      )}
      {isAdmin && s === "EXTERNAL_VERIFICATION" && (
        <Button size="sm" data-testid="record-verification-btn" onClick={() => setVerifyOpen(true)}>
          <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Record verification
        </Button>
      )}
      {isAdmin && s === "APPROVED" && (
        <Button size="sm" data-testid="issue-epd-btn" onClick={() => act(() => api.post(`/epds/${epdId}/issue`), "EPD issued")}>
          <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Issue EPD
        </Button>
      )}
      {s === "ISSUED" && (
        <>
          <Button size="sm" variant="outline" data-testid="final-pdf-btn" onClick={() => openPdf(`/epds/${epdId}/pdf`)}><FileDown className="h-3.5 w-3.5 mr-1" /> Final PDF</Button>
          <a href={`/verify/${ver.epd_public_id}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid="public-page-btn">Public page</Button></a>
          {isManager && <Button size="sm" data-testid="new-version-btn" onClick={() => act(() => api.post(`/epds/${epdId}/new-version`), "New version created")}><GitBranch className="h-3.5 w-3.5 mr-1" /> New version</Button>}
        </>
      )}
      <VerificationDialog open={verifyOpen} onOpenChange={setVerifyOpen} epdId={epdId} onChange={onChange} />
      <IndependentVerificationDialog open={indepOpen} onOpenChange={setIndepOpen} epdId={epdId} onChange={onChange} />
      </div>
    </div>
  );
}

/* ---------- Platform Validation panel (R1.3A-02: backend-derived gate states) ---------- */
function PlatformValidationPanel({ pv }) {
  if (!pv) return null;
  const TONE = {
    PASSED: "bg-emerald-50 border-emerald-200 text-emerald-700",
    ACTION_REQUIRED: "bg-red-50 border-red-200 text-red-700",
    WARNING: "bg-amber-50 border-amber-200 text-amber-700",
    IN_PROGRESS: "bg-sky-50 border-sky-200 text-sky-700",
    NOT_YET_SUBMITTED: "bg-slate-100 border-slate-200 text-slate-600",
    NOT_YET_EVALUATED: "bg-slate-100 border-slate-200 text-slate-600",
  };
  const LABEL = { PASSED: "Passed", ACTION_REQUIRED: "Action required", WARNING: "Warning",
    IN_PROGRESS: "In progress", NOT_YET_SUBMITTED: "Not yet submitted", NOT_YET_EVALUATED: "Not yet evaluated" };
  return (
    <div data-testid="platform-validation-panel" className="rounded-lg border p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-sm">Platform Validation</h3>
        <span data-testid="platform-validated-flag"
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${pv.platform_validated ? TONE.PASSED : TONE.ACTION_REQUIRED}`}>
          {pv.platform_validated ? "PLATFORM VALIDATED" : "Not yet Platform Validated"}
        </span>
      </div>
      <ul className="space-y-1.5">
        {pv.gates.map((gt) => (
          <li key={gt.key} data-testid={`pv-gate-${gt.key}`} className="flex items-start justify-between gap-3 text-sm">
            <div>
              <span>{gt.label}</span>
              {gt.reason && <span data-testid={`pv-reason-${gt.key}`} className="block text-xs text-muted-foreground">{gt.reason}</span>}
            </div>
            <span className={`shrink-0 inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-medium ${TONE[gt.state] || TONE.NOT_YET_EVALUATED}`}
              data-testid={`pv-state-${gt.key}`}>{LABEL[gt.state] || gt.state}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- Submit for Independent Verification (R1.3A-02 -> R1.2C-02 workflow) ---------- */
function IndependentVerificationDialog({ open, onOpenChange, epdId, onChange }) {
  const [verifiers, setVerifiers] = useState([]);
  const [route, setRoute] = useState("THIRD_PARTY_INDIVIDUAL");
  const [verifierId, setVerifierId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/verification/verifiers").then(({ data }) => setVerifiers(data || [])).catch(() => setVerifiers([]));
  }, [open]);
  const submit = async () => {
    setBusy(true);
    try {
      const { data: a } = await api.post(`/epds/${epdId}/verification/assignment`,
        { verification_route: route, verifier_id: verifierId });
      await api.post(`/verification/${a.assignment_id}/submit`);
      toast.success("Submitted for independent verification");
      onOpenChange(false); onChange();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="independent-verification-dialog">
        <DialogHeader><DialogTitle>Submit for Independent Verification</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">This submits the exact platform-validated EPD version into the independent third-party verification workflow. Platform validation is not the same as independent verification, and you cannot verify your own EPD.</p>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Verification route *</Label>
            <select data-testid="indep-route" value={route} onChange={(e) => setRoute(e.target.value)}
              className="mt-1 w-full rounded-md border px-2 py-1.5 text-sm">
              <option value="THIRD_PARTY_INDIVIDUAL">Third-party individual verifier</option>
              <option value="THIRD_PARTY_CERTIFICATION_BODY">Accredited certification body</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Approved verifier *</Label>
            <select data-testid="indep-verifier" value={verifierId} onChange={(e) => setVerifierId(e.target.value)}
              className="mt-1 w-full rounded-md border px-2 py-1.5 text-sm">
              <option value="">Select a verifier…</option>
              {verifiers.map((v) => (
                <option key={v.verifier_id} value={v.verifier_id}>
                  {v.full_name || v.organisation || v.verifier_id} — {v.qualification_status}{v.is_preview ? " (PREVIEW)" : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="indep-submit" onClick={submit} disabled={busy || !verifierId}>Submit for verification</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VerificationDialog({ open, onOpenChange, epdId, onChange }) {
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      let fileId = form.verification_doc_file_id;
      if (file) {
        const fd = new FormData(); fd.append("file", file); fd.append("kind", "verification");
        const { data } = await api.post("/uploads", fd); fileId = data.file_id;
      }
      await api.post(`/epds/${epdId}/verification`, { ...form, verification_doc_file_id: fileId });
      toast.success("Independent verification recorded");
      onOpenChange(false); onChange();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Record independent external verification</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">Only enter legitimate, externally-performed verification details. The EPD will display "Independently Verified" only after this is recorded with supporting documentation.</p>
        <div className="space-y-3 mt-2">
          <div><Label className="text-xs">Verifier name *</Label><Input data-testid="ver-name" value={form.verifier_name || ""} onChange={(e) => set("verifier_name", e.target.value)} className="mt-1" /></div>
          <div><Label className="text-xs">Verifier organisation *</Label><Input data-testid="ver-org" value={form.verifier_org || ""} onChange={(e) => set("verifier_org", e.target.value)} className="mt-1" /></div>
          <div><Label className="text-xs">Verifier credentials / reference</Label><Input data-testid="ver-cred" value={form.verifier_credentials || ""} onChange={(e) => set("verifier_credentials", e.target.value)} className="mt-1" /></div>
          <div><Label className="text-xs">Verification date *</Label><Input data-testid="ver-date" type="date" value={form.verification_date || ""} onChange={(e) => set("verification_date", e.target.value)} className="mt-1" /></div>
          <div><Label className="text-xs">Verification statement *</Label><Textarea data-testid="ver-statement" value={form.verification_statement || ""} onChange={(e) => set("verification_statement", e.target.value)} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Programme operator</Label><Input data-testid="ver-programme" value={form.programme_operator || ""} onChange={(e) => set("programme_operator", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs">Programme reg. no.</Label><Input data-testid="ver-reg" value={form.programme_registration || ""} onChange={(e) => set("programme_registration", e.target.value)} className="mt-1" /></div>
          </div>
          <div><Label className="text-xs">Verification documentation * (upload)</Label><Input data-testid="ver-doc" type="file" onChange={(e) => setFile(e.target.files[0])} className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="ver-submit" onClick={submit} disabled={busy || !form.verifier_name || !form.verifier_org || !form.verification_date || !form.verification_statement || !file}>Record verification</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fmt(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (n === 0) return "0";
  if (Math.abs(n) >= 100) return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  if (Math.abs(n) >= 1) return n.toFixed(3);
  return n.toExponential(2);
}
